import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { displayDate } from '../common/datePresentation';
import { formatLedgerAmount } from '../manage/clientLedgerTypes';
import SettlementExtraFeeForm from './SettlementExtraFeeForm';

export default function SettlementExtraFees({ settlement, state, students, onMutate }) {
  const { t, i18n } = useTranslation();
  const tr = key => t(`billing:fees.${key}`);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const request = useRef(null);
  const items = state ? state.items : (settlement.snapshot.extraFees || []);
  async function mutate(action, fee, feeId, correctionReason) {
    if (inFlight.current) throw new Error('FEE_SAVE_IN_PROGRESS');
    inFlight.current = true; setBusy(true);
    const body = { action, fee, feeId, correctionReason, expectedBillingRevision: state.expectedBillingRevision };
    const key = JSON.stringify(body);
    if (!request.current || request.current.key !== key) request.current = { key, id: `fee-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    try {
      await onMutate({ ...body, requestId: request.current.id });
      request.current = null; setEditing(null); setPending(null); setError('');
    } finally { setBusy(false); inFlight.current = false; }
  }
  async function confirm() {
    if (inFlight.current) return;
    if (settlement.approved && (!reason.trim() || reason.length > 1000)) { setError(tr('invalidReason')); return; }
    setError('');
    try { await mutate(pending.action, undefined, pending.feeId, reason.trim()); } catch (failure) { setError(tr(failure.feesRefreshed ? 'conflict' : 'saveError')); }
  }
  return (
    <section className="settlement-extra-fees mb-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap">
        <strong>{tr('title')} {state && state.hasDraft && <small className="text-muted">· {tr('draft')}</small>}</strong>
        {state && <button className="btn btn-sm manage-teal-button" type="button" disabled={busy} onClick={() => { setEditing({}); setPending(null); }}>＋ {tr('add')}</button>}
      </div>
      {!state && <p className="text-muted small">{tr('loading')}</p>}
      {items.map(item => (
        <div key={item.feeId} className="d-flex align-items-center flex-wrap border-bottom py-2">
          <span className="mr-3">{item.description} <small className="text-muted">{item.studentNameSnapshot || tr('wholeClient')} · {tr(item.category)} · {displayDate(item.date, 'pl')}</small></span>
          <strong className="ml-auto mr-3">{formatLedgerAmount(item.amountCents / 100, i18n.language)}</strong>
          {state && (
          <><button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { setEditing(item); setPending(null); }}>{tr(settlement.approved ? 'correct' : 'edit')}</button>
            <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { setPending({ action: 'remove', feeId: item.feeId }); setEditing(null); }}>{tr(settlement.approved ? 'withdraw' : 'remove')}</button>
          </>
          )}
        </div>
      ))}
      {!items.length && <p className="small text-muted mb-2">{tr('empty')}</p>}
      {state && state.hasDraft && <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => { setPending({ action: 'discardDraft' }); setEditing(null); }}>{tr('discard')}</button>}
      {editing && state && (
      <SettlementExtraFeeForm
        key={editing.feeId || 'new'}
        month={settlement.month}
        students={students}
        initialFee={editing.feeId ? editing : null}
        idPrefix={`fee-${settlement.clientId}`}
        correctionRequired={settlement.approved}
        initialReason={state.correctionReason || ''}
        onCancel={() => setEditing(null)}
        onSave={(fee, correctionReason) => mutate(editing.feeId ? 'update' : 'add', fee, editing.feeId, correctionReason)}
      />
      )}
      {pending && (
      <div className="border p-3 mt-2">
        <p>{tr(pending.action === 'remove' ? 'removeConfirm' : 'discardConfirm')}</p>
        {settlement.approved && (
        <label className="d-block" htmlFor={`fee-${settlement.clientId}-withdraw-reason`}>{tr('reason')}
          <input id={`fee-${settlement.clientId}-withdraw-reason`} className="form-control" value={reason} onChange={event => setReason(event.target.value)} />
        </label>
        )}
        {error && <div className="text-danger" role="alert">{error}</div>}
        <button type="button" className="btn manage-teal-button mr-2 mb-2" disabled={busy} onClick={() => setPending(null)}>{tr('cancel')}</button>
        <button type="button" className="btn manage-red-button mb-2" disabled={busy} onClick={confirm}>{tr(busy ? 'saving' : 'confirm')}</button>
      </div>
      )}
      <small className="text-muted d-block mt-2">{tr('hint')}</small>
    </section>
  );
}
SettlementExtraFees.propTypes = { settlement: PropTypes.object.isRequired, state: PropTypes.object, students: PropTypes.array.isRequired, onMutate: PropTypes.func.isRequired };
SettlementExtraFees.defaultProps = { state: null };
